import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Emblem } from '../../components/Emblem';
import { getOrder, type OrderSummary } from '../../api/orders';
import { formatGroszeAsZl } from '../../util/money';
import { useShop } from '../ShopState';
import { OrderLookupForm } from '../OrderLookupForm';
import { ProductSpec } from '../ProductSpec';
import { StripePayButton } from '../StripePayButton';
import { SETTLEMENT_COPY, isPaymentConfirmed, usePaymentSettlement } from '../paymentSettlement';
import { COLOR_META } from '../colors';

export function Success() {
  const { orderId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState<OrderSummary | null>(null);
  const [denied, setDenied] = useState(false);
  const { catalog, lastOrder, resetOrderSession } = useShop();
  const location = useLocation();
  const placedOrderId = (location.state as { placedOrderId?: string } | null)?.placedOrderId;
  // The link carries the token; fall back to the one kept in memory from checkout.
  const token = searchParams.get('t') ?? (lastOrder && lastOrder.id === orderId ? lastOrder.token : null);
  // Stripe's return trip cannot carry router state, so the payment outcome arrives as a query
  // marker. Read once: the marker is stripped from the URL below, and a reload of the cleaned
  // link must not resurrect it.
  const [paymentReturn] = useState(() => searchParams.get('platnosc'));

  useEffect(() => {
    if (!orderId || !token) return;
    let stop = false;
    getOrder(orderId, token)
      .then((o) => !stop && setOrder(o))
      .catch(() => !stop && setDenied(true));
    return () => {
      stop = true;
    };
  }, [orderId, token]);

  // Only the order just placed ends the session, plus the trip back from Stripe — by then the
  // order exists and the basket that built it must not survive into a second one. Reaching this
  // page any other way — the confirmation email, the surname lookup — is someone reading an
  // order, and must not touch a checkout in progress. The marker is consumed immediately:
  // history entries keep their state, so Back could otherwise replay this on a later basket.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const backFromStripe = params.has('platnosc');
    if (!orderId || (placedOrderId !== orderId && !backFromStripe)) return;
    resetOrderSession();
    params.delete('platnosc');
    const search = params.toString();
    navigate(`${location.pathname}${search ? `?${search}` : ''}${location.hash}`, {
      replace: true,
      state: null,
    });
  }, [
    orderId,
    placedOrderId,
    resetOrderSession,
    navigate,
    location.pathname,
    location.search,
    location.hash,
  ]);

  const isPaid = isPaymentConfirmed(order);
  const isAwaiting = order?.status === 'awaiting_payment';
  const settlement = usePaymentSettlement({
    orderId,
    token,
    order,
    onOrder: setOrder,
    returnedFromPayment: paymentReturn === 'ok',
  });
  const productById = new Map(
    catalog.status === 'ready' ? catalog.products.map((p) => [p.id, p]) : [],
  );

  // No usable token, or the server rejected it: offer the surname lookup instead
  // of a blank page.
  if (!token || denied) {
    return (
      <div className="phone-screen">
        <div style={{ padding: '40px 22px', textAlign: 'center' }}>
          <Emblem size={110} style={{ margin: '0 auto' }} />
          <h1 className="display" style={{ fontSize: 24, marginTop: 16, color: 'var(--orange)' }}>
            POTWIERDŹ ZAMÓWIENIE
          </h1>
          <p style={{ marginTop: 10, fontSize: 13, color: 'rgba(var(--shop-muted-rgb),.75)' }}>
            Ten link nie zawiera ważnego dostępu do zamówienia. Podaj numer i nazwisko.
          </p>
          <OrderLookupForm />
        </div>
      </div>
    );
  }

  return (
    <div className="phone-screen">
      <div style={{ padding: '40px 22px 0', textAlign: 'center' }}>
        <div>
          <Emblem size={120} style={{ margin: '0 auto' }} />
        </div>
        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center' }}>
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 10,
              padding: '8px 16px',
              border: '2.5px solid var(--orange)',
              background: 'rgba(255,107,26,.08)',
              color: 'var(--orange)',
              fontFamily: 'var(--display)',
              fontSize: 16,
              letterSpacing: '.22em',
              transform: 'rotate(-2.5deg)',
              boxShadow: '0 0 0 4px var(--shop-bg), 0 0 0 5px rgba(255,107,26,.35)',
            }}
          >
            <span style={{ width: 10, height: 10, borderRadius: 999, background: 'var(--orange)' }} />
            {isPaid ? 'ZAPŁACONE' : settlement ? 'PŁATNOŚĆ W TOKU' : 'CZEKA NA PŁATNOŚĆ'}
          </div>
        </div>
        <div className="display" style={{ fontSize: 40, lineHeight: 0.95, marginTop: 22, color: 'var(--orange)' }}>
          {isPaid ? 'DZIĘKUJEMY' : settlement ? 'SPRAWDZAMY PŁATNOŚĆ' : 'DOKOŃCZ PŁATNOŚĆ'}
        </div>
      </div>

      <div style={{ padding: '24px 22px 0' }}>
        {(settlement || (isAwaiting && paymentReturn === 'anulowana')) && (
          <div
            className="mono"
            style={{
              marginBottom: 14,
              padding: '10px 12px',
              border: '1px dashed var(--orange)',
              color: 'var(--orange)',
              fontSize: 11,
              lineHeight: 1.5,
            }}
          >
            {settlement
              ? SETTLEMENT_COPY[settlement]
              : 'PŁATNOŚĆ NIE ZOSTAŁA DOKOŃCZONA. ZAMÓWIENIE CZEKA - MOŻESZ ZAPŁACIĆ PONIŻEJ.'}
          </div>
        )}
        {isAwaiting && !settlement && order && token && (
          <StripePayButton order={order} token={token} compact />
        )}

        <div
          style={{
            background: 'var(--shop-card-bg)',
            border: '2px solid rgba(var(--shop-muted-rgb),.15)',
            padding: 14,
          }}
        >
          <div className="mono" style={{ fontSize: 10, letterSpacing: '.15em', color: 'var(--orange)' }}>
            NUMER ZAMÓWIENIA
          </div>
          <div className="display" style={{ fontSize: 24, marginTop: 4 }}>
            {orderId}
          </div>
          {order && (
            <>
              <div className="divider-dashed" style={{ margin: '12px 0' }} />
              {order.items.map((item, i) => {
                const product = productById.get(item.productId);
                const colorLabel = product ? `${COLOR_META[product.color].label.toLowerCase()} · ` : '';
                return (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 12, marginBottom: 4 }}>
                    <span style={{ minWidth: 0 }}>
                      Koszulka · {colorLabel}
                      {item.cut} · {item.size}
                      {item.qty > 1 ? ` × ${item.qty}` : ''}
                      <ProductSpec product={product} fontSize={10} />
                    </span>
                    <span className="mono" style={{ flexShrink: 0 }}>{formatGroszeAsZl(item.qty * item.unitPriceGrosze)}</span>
                  </div>
                );
              })}
              {order.shippingMethod === 'zagranica' ? (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginTop: 4 }}>
                  <span>
                    Wysyłka zagraniczna
                    {order.shippingAddress.international?.countryName
                      ? ` · ${order.shippingAddress.international.countryName}`
                      : ''}
                  </span>
                  <span className="mono">USTALANA OSOBNO</span>
                </div>
              ) : (
                order.shippingMethod !== 'odbior' && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginTop: 4 }}>
                    <span>{order.shippingMethod === 'paczkomat' ? 'Paczkomat InPost' : 'Kurier InPost'}</span>
                    <span className="mono">{formatGroszeAsZl(order.shippingCostGrosze)}</span>
                  </div>
                )
              )}
              <div className="divider-dashed" style={{ margin: '10px 0' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span className="display" style={{ fontSize: 14 }}>
                  RAZEM
                </span>
                <span className="display" style={{ fontSize: 18, color: 'var(--orange)' }}>
                  {formatGroszeAsZl(order.totalGrosze).toUpperCase()}
                </span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
